import React from 'react';
import { useInViewAnimation } from '../hooks/useInViewAnimation';
import {
  Sparkles, Building2, Cloud, TrendingUp, ShieldCheck,
  Users, MessageSquare, Infinity as InfinityIcon, Rocket
} from 'lucide-react';

const reasons = [
  { icon: Sparkles, title: 'AI First', desc: 'AI is not a bolt-on. It sits at the core of every product we design and ship.' },
  { icon: Building2, title: 'Enterprise Ready', desc: 'Built for regulated, high-stakes environments &mdash; audit trails, SSO, SLAs and support.' },
  { icon: Cloud, title: 'Cloud Native', desc: 'AWS, Azure, GCP. Kubernetes-ready. Multi-region by default when it matters.' },
  { icon: TrendingUp, title: 'Scalable Systems', desc: 'Architectures that hold up when your business scales 10x &mdash; not just at demo day.' },
  { icon: ShieldCheck, title: 'Secure Development', desc: 'Security in the SDLC. SOC2, ISO 27001, HIPAA, GDPR-ready from day one.' },
  { icon: Users, title: 'Dedicated Engineering Team', desc: 'A senior pod that stays with you &mdash; no rotating faces, no lost context.' },
  { icon: MessageSquare, title: 'Transparent Communication', desc: 'Weekly demos, live environments, honest status. No theatre, no surprises.' },
  { icon: InfinityIcon, title: 'Long-Term Partnership', desc: 'We stay after launch. Roadmap, iterate, evolve &mdash; year after year.' },
  { icon: Rocket, title: 'Agile Delivery', desc: 'Shipping value every week, not once a quarter. Real velocity, not framework theatre.' },
];

export function WhyUsSection() {
  const { ref, isInView } = useInViewAnimation();

  return (
    <section
      ref={ref}
      data-testid="why-us-section"
      className="max-w-[1200px] mx-auto px-6 py-16 md:py-20"
    >
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-10 md:mb-14">
        <h2
          className={`text-[32px] md:text-[40px] lg:text-[44px] leading-[1.1] text-[#0D212C] tracking-tight ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
          style={{ animationDelay: '0.05s' }}
        >
          Why <span className="font-serif grad-text">YP Nova</span>
        </h2>
        <p
          className={`text-sm md:text-base text-[#273C46] max-w-md ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
          style={{ animationDelay: '0.15s' }}
        >
          Nine reasons enterprise teams &mdash; from manufacturing to healthcare &mdash; choose us over the big consultancies.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {reasons.map(({ icon: Icon, title, desc }, i) => (
          <div
            key={title}
            data-testid={`why-card-${i}`}
            className={`group rounded-[28px] bg-white border border-[#7C3AED]/10 p-8 shadow-[0_4px_16px_rgba(0,0,0,0.04)] hover:shadow-[0_10px_40px_rgba(109,40,217,0.20)] hover:border-[#7C3AED]/40 transition-all duration-300 hover:-translate-y-1 ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
            style={{ animationDelay: `${0.1 + i * 0.05}s` }}
          >
            <div className="w-11 h-11 rounded-2xl grad-icon text-white flex items-center justify-center shadow-[0_6px_20px_rgba(109,40,217,0.28)] mb-5 group-hover:-rotate-6 transition-transform">
              <Icon className="w-4 h-4" />
            </div>
            <h3 className="font-serif text-xl text-[#0D212C] mb-2">{title}</h3>
            <p
              className="text-sm text-[#273C46] leading-relaxed"
              dangerouslySetInnerHTML={{ __html: desc }}
            />
          </div>
        ))}
      </div>
    </section>
  );
}
